import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MealItem from '../../../app/data/models/mealItem';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import StyledTableRow from '../../../app/components/StyledTableRow';
import FillWidthCell from '../../../app/components/FillWidthCell';
import EmptyScreen from '../../../app/components/EmptyScreen';
import {
  fetchKitchenMealItemsAsync,
  updateMealItemAvailabilityAsync,
} from './kitchenMealItemsSlice';
import { toast } from 'react-toastify';

const KitchenStaffMealItemList = () => {
  const { t } = useTranslation();
  const { kitchenId: kitchenIdString } = useParams<{
    kitchenId: string;
  }>();
  const kitchenId = kitchenIdString || '';
  const { mealItems, nextPageId, status } = useAppSelector(
    (state) => state.kitchenMealItems
  );
  const dispatch = useAppDispatch();
  const [loadingItemId, setLoadingItemId] = useState<string | null>(null);
  const [pageHistory, setPageHistory] = useState<string[]>([]);

  useEffect(() => {
    dispatch(fetchKitchenMealItemsAsync({ kitchenId }));
  }, [kitchenId, dispatch]);

  const handleToggleAvailability = (
    id: string,
    currentAvailability: boolean
  ) => {
    setLoadingItemId(id);
    dispatch(
      updateMealItemAvailabilityAsync({
        kitchenId,
        mealItemId: id,
        isAvailable: !currentAvailability,
      })
    )
      .unwrap()
      .then(() => {
        // Refresh the meal items list after updating
        dispatch(
          fetchKitchenMealItemsAsync({
            kitchenId,
            nextPageId: pageHistory[pageHistory.length - 1],
          })
        );
        toast.success(
          t(
            !currentAvailability
              ? 'kitchen-staff.meal-item.availability.enabled'
              : 'kitchen-staff.meal-item.availability.disabled',
            !currentAvailability
              ? 'Item is now available'
              : 'Item is now unavailable'
          )
        );
      })
      .catch((error) => {
        toast.error(
          t(
            'kitchen-staff.meal-item.availability.error',
            'Failed to update availability'
          )
        );
        console.error('Error updating availability:', error);
      })
      .finally(() => {
        setLoadingItemId(null);
      });
  };

  const handleMealItemClick = (mealItem: MealItem) => {
    // Do nothing for now - just show availability toggle
    console.log('Selected meal item:', mealItem.id);
  };

  const handleNextPage = () => {
    if (nextPageId) {
      setPageHistory((prev) => [...prev, nextPageId]);
      dispatch(fetchKitchenMealItemsAsync({ kitchenId, nextPageId }));
    }
  };

  const handlePreviousPage = () => {
    if (pageHistory.length > 0) {
      const newHistory = [...pageHistory];
      newHistory.pop();
      const previousPageId = newHistory[newHistory.length - 1];
      setPageHistory(newHistory);
      dispatch(
        fetchKitchenMealItemsAsync({ kitchenId, nextPageId: previousPageId })
      );
    }
  };

  if (status.includes('pending') && status !== 'pendingUpdateAvailability') {
    return <LoadingComponent />;
  }

  if (!mealItems || mealItems.length === 0)
    return (
      <EmptyScreen
        title={t('kitchen-staff.meal-item.list.title', 'Kitchen Meal Items')}
        emptyText={t(
          'kitchen-staff.meal-item.list.empty',
          'No meal items found for this kitchen'
        )}
      />
    );

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <ListTitleBox
        title={t('kitchen-staff.meal-item.list.title', 'Kitchen Meal Items')}
      />
      <TableContainer component={Paper} sx={{ width: '100%', mb: 2 }}>
        <Table
          aria-label={t(
            'kitchen-staff.meal-item.list.title',
            'Kitchen Meal Items'
          )}
        >
          <TableHead>
            <TableRow>
              <TableCell width="50px" align="left">
                #
              </TableCell>
              <FillWidthCell align="left">
                {t('meal-item.list.header.name', 'Name')}
              </FillWidthCell>
              <TableCell align="center">
                {t(
                  'kitchen-staff.meal-item.list.header.availability',
                  'Availability'
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mealItems.map((mealItem, index) => (
              <StyledTableRow
                key={mealItem.id}
                onClick={() => handleMealItemClick(mealItem)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  width: '100%',
                }}
              >
                <TableCell width="50px" align="left" component="th" scope="row">
                  {index + 1}
                </TableCell>
                <TableCell align="left">{mealItem.name}</TableCell>
                <TableCell align="center">
                  <FormControlLabel
                    control={
                      <Box
                        position="relative"
                        display="inline-flex"
                        alignItems="center"
                      >
                        {loadingItemId !== mealItem.id && (
                          <Switch
                            checked={mealItem.isAvailable || false}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleAvailability(
                                mealItem.id,
                                mealItem.isAvailable || false
                              );
                            }}
                            disabled={loadingItemId === mealItem.id}
                            color="primary"
                          />
                        )}
                        {loadingItemId === mealItem.id && (
                          <CircularProgress
                            size={24}
                            sx={{
                              mx: 2,
                            }}
                          />
                        )}
                      </Box>
                    }
                    label={
                      mealItem.isAvailable
                        ? t('kitchen-staff.meal-item.available', 'Available')
                        : t(
                            'kitchen-staff.meal-item.unavailable',
                            'Unavailable'
                          )
                    }
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      mx: 1,
                    }}
                  />
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 2 }}>
        <IconButton
          onClick={handlePreviousPage}
          disabled={pageHistory.length === 0}
          sx={{
            backgroundColor: (theme) => theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: (theme) =>
                theme.palette.action.disabledBackground,
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={handleNextPage}
          disabled={!nextPageId}
          sx={{
            backgroundColor: (theme) => theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: (theme) =>
                theme.palette.action.disabledBackground,
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default KitchenStaffMealItemList;
